import { useState, useRef, useCallback } from 'react'
import { Alert, CircularProgress } from '@mui/material'
import { Icon } from '@/components/ui'
import { useT } from '@/i18n/useT'

interface DropzoneProps {
  onFileSelect: (file: File) => void
  isUploading?: boolean
  uploadProgress?: number
  accept?: string
}

const VALID_EXTENSIONS = ['.pdf', '.png', '.jpg', '.jpeg', '.xml']
const MAX_SIZE = 10 * 1024 * 1024

/**
 * The design's dashed accent panel. It fills the same slot as the processing
 * card next to it, so the two swap without the layout jumping.
 */
export function DocumentDropzone({
  onFileSelect,
  isUploading = false,
  uploadProgress = 0,
  accept = '.pdf,.png,.jpg,.jpeg,.xml',
}: DropzoneProps) {
  const { t } = useT()
  const [isDragging, setIsDragging] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const validateFile = (file: File): boolean => {
    const extension = '.' + file.name.split('.').pop()?.toLowerCase()
    if (!VALID_EXTENSIONS.includes(extension)) {
      setError(t('Invalid file type. Please upload PDF, PNG, JPG or XML.'))
      return false
    }
    if (file.size > MAX_SIZE) {
      setError(t('File too large. Maximum size is 10 MB.'))
      return false
    }
    setError(null)
    return true
  }

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      setIsDragging(false)
      if (isUploading) return
      const file = e.dataTransfer.files[0]
      if (file && validateFile(file)) onFileSelect(file)
    },
    [isUploading, onFileSelect, t],
  )

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      if (!isUploading) setIsDragging(true)
    },
    [isUploading],
  )

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file && validateFile(file)) onFileSelect(file)
      if (inputRef.current) inputRef.current.value = ''
    },
    [onFileSelect, t],
  )

  return (
    <div style={{ flex: '1 1 340px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={() => setIsDragging(false)}
        onClick={() => !isUploading && inputRef.current?.click()}
        style={{
          flex: 1,
          border: '2px dashed var(--accentBorder)',
          borderRadius: 12,
          background: isDragging ? 'var(--accentSoft)' : 'var(--paper)',
          padding: 32,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 10,
          textAlign: 'center',
          cursor: isUploading ? 'wait' : 'pointer',
          transition: 'background-color .15s ease',
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          style={{ display: 'none' }}
          onChange={handleFileSelect}
          disabled={isUploading}
        />

        {isUploading ? (
          <>
            <CircularProgress
              size={40}
              variant={uploadProgress > 0 ? 'determinate' : 'indeterminate'}
              value={uploadProgress}
            />
            <div style={{ fontSize: 15, fontWeight: 700 }}>{t('Uploading document …')}</div>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>
              {uploadProgress > 0 ? `${Math.round(uploadProgress)} %` : t('Please wait')}
            </div>
          </>
        ) : (
          <>
            <Icon name="upload" size={30} strokeWidth={1.8} style={{ color: 'var(--accent)' }} />
            <div style={{ fontSize: 15, fontWeight: 700 }}>
              {t('Drop invoice here or click to browse')}
            </div>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>
              PDF, PNG, JPG, ZUGFeRD/XRechnung XML · {t('max 10 MB')}
            </div>
          </>
        )}
      </div>

      {error && <Alert severity="error">{error}</Alert>}
    </div>
  )
}
